import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  CarbonFootprintResponse,
  Cycle,
  CycleModuleState,
  Delivery,
  Product,
} from '../../models/cycle.model';
import { Concept, SlaughterReport } from '../../models/flock-registration-service.model';
import * as cycleActions from '../actions/cycle.actions';

function selectIdNumber(c: Cycle): number {
  return c.id;
}

function sortByDate(a: Cycle, b: Cycle): number {
  return new Date(b.cycleDate).getTime() / 1000 - new Date(a.cycleDate).getTime() / 1000;
}

function getLatestConcept(state: CycleModuleState, cycleId: number): Concept {
  const cycle = state.cycles.entities[cycleId];
  const conceptId = cycle.concept.id;
  return state.concepts.find((c) => c.id === conceptId);
}

export const adapter: EntityAdapter<Cycle> = createEntityAdapter<Cycle>({
  selectId: selectIdNumber,
  sortComparer: sortByDate,
});

export const initialState = <CycleModuleState>{
  cycles: adapter.getInitialState([]),
  selectedCycleId: 0,
  isDetailedOverview: false,
  routeTransitionBalance: null,
  isLoaded: false,
  slaughterInvoices: {
    linked: [],
    unlinked: [],
  },
  sustainabilityNoticeVisible: true,
};

const cycleReducer = createReducer(
  initialState,
  on(cycleActions.clearCycleState, (state) => ({
    ...state,
    selectedCycleId: 0,
    isLoaded: false,
    deliveries: undefined as Delivery[],
    slaughterReports: undefined as SlaughterReport[],
    cycles: adapter.removeAll(state.cycles),
  })),
  on(cycleActions.loadCyclesSuccess, (state, { cycles }) => ({
    ...state,
    selectedCycleId: 0,
    isLoaded: true,
    cycles: adapter.setAll(cycles, state.cycles),
  })),
  on(cycleActions.upsertCycleSuccess, (state, { cycle }) => ({
    ...state,
    selectedProduct: undefined as Product,
    cycles: adapter.upsertOne(cycle, state.cycles),
    selectedCycleId: cycle.id,
  })),
  on(cycleActions.deleteCycleSuccess, (state, { cycle }) => ({
    ...state,
    selectedProduct: undefined as Product,
    selectedCycleId: 0,
    cycles: adapter.removeOne(cycle.id, state.cycles),
  })),
  on(cycleActions.loadConceptsSuccess, (state, { concepts }) => ({
    ...state,
    concepts,
  })),
  on(cycleActions.loadDeliveriesSuccess, (state, { deliveries }) => ({
    ...state,
    deliveries: deliveries?.length > 0 ? deliveries : [],
  })),
  on(cycleActions.loadHatcheriesSuccess, (state, { hatcheries }) => ({
    ...state,
    hatcheries,
  })),
  on(cycleActions.loadSlaughterhousesSuccess, (state, { slaughterhouses }) => ({
    ...state,
    slaughterhouses,
  })),
  on(cycleActions.loadSlaughterReportsSuccess, (state, { slaughterReports }) => ({
    ...state,
    slaughterReports,
  })),
  on(cycleActions.loadSlaughterInvoicesSuccess, (state, { isLinked, slaughterInvoices }) => ({
    ...state,
    slaughterInvoices: {
      linked: isLinked ? slaughterInvoices : state.slaughterInvoices.linked,
      unlinked: !isLinked ? slaughterInvoices : state.slaughterInvoices.unlinked,
    },
  })),
  on(cycleActions.loadProductsRequest, (state) => ({
    ...state,
    products: undefined as Product[],
  })),
  on(cycleActions.loadProductsSuccess, (state, { products }) => ({
    ...state,
    products,
  })),
  on(cycleActions.calculateBalanceSuccess, (state, { cycle }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycle.id,
        changes: cycle,
      },
      state.cycles,
    ),
  })),
  on(cycleActions.addDayOldChick, (state, { cycleId, dayOldChick }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          dayOldChicks: [...(state.cycles.entities[cycleId].dayOldChicks || []), dayOldChick],
          concept: getLatestConcept(state, cycleId),
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editDayOldChick, (state, { cycleId, dayOldChick }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          dayOldChicks: [
            ...state.cycles.entities[cycleId].dayOldChicks.filter((c) => c.id !== dayOldChick.id),
            dayOldChick,
          ],
          concept: getLatestConcept(state, cycleId),
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteDayOldChick, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          dayOldChicks: [...state.cycles.entities[cycleId].dayOldChicks.filter((c) => c.id !== id)],
          concept: getLatestConcept(state, cycleId),
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.addDeparture, (state, { cycleId, departure }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [...(state.cycles.entities[cycleId].departures || []), departure],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editDeparture, (state, { cycleId, departure }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [
            ...state.cycles.entities[cycleId].departures.filter((c) => c.id !== departure.id),
            departure,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteDeparture, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          departures: [...state.cycles.entities[cycleId].departures.filter((c) => c.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.addBalanceRow, (state, { cycleId, balanceRow }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          balancesPerDeliveryLine: [
            ...(state.cycles.entities[cycleId].balancesPerDeliveryLine || []),
            balanceRow,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editBalanceRow, (state, { cycleId, balanceRow }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          balancesPerDeliveryLine: [
            ...(state.cycles.entities[cycleId].balancesPerDeliveryLine || []).filter(
              (c) =>
                c.deliveryId !== balanceRow.deliveryId &&
                c.deliveryLineIdentifier !== balanceRow.deliveryLineIdentifier,
            ),
            balanceRow,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteBalanceRow, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          balancesPerDeliveryLine: state.cycles.entities[cycleId].balancesPerDeliveryLine.filter(
            (c) => `${c.deliveryId}_${c.deliveryLineIdentifier}` !== id,
          ),
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.upsertCycleGeneral, (state, { cycle }) => ({
    ...state,
    cycles: adapter.upsertOne(cycle, state.cycles),
  })),
  on(cycleActions.upsertCosts, (state, { cycleId, costs }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          costs,
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedProduct, (state, { product }) => ({
    ...state,
    selectedProduct: product || undefined,
  })),
  on(cycleActions.setIsCycleDetailedOverview, (state, { value }) => ({
    ...state,
    isDetailedOverview: value,
  })),
  on(cycleActions.setSelectedCycle, (state, { cycleId }) => ({
    ...state,
    selectedCycleId: cycleId,
  })),
  // on(cycleActions.calculateBalanceSuccess, (state, { cycle }) => ({
  //   ...state,
  //   selectedProduct: undefined,
  //   cycles: adapter.updateOne(
  //     {
  //       id: cycle.id,
  //       changes: {
  //         balancesPerDeliveryLine: cycle.balancesPerDeliveryLine,
  //       },
  //     },
  //     state.cycles,
  //   ),
  //   selectedCycleId: cycle.id,
  // })),
  on(
    cycleActions.setRouteTransitionBalance,
    (state, { routeTransitionBalance: routeTransitionBalance }) => ({
      ...state,
      routeTransitionBalance,
    }),
  ),
  on(cycleActions.addDiscount, (state, { cycleId, discount }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          discounts: [...(state.cycles.entities[cycleId].discounts || []), discount],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.addDiscounts, (state, { cycleId, discounts }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          discounts: [...(state.cycles.entities[cycleId].discounts || []), ...discounts],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.editDiscount, (state, { cycleId, discount }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          discounts: [
            ...state.cycles.entities[cycleId].discounts.filter((d) => d.id !== discount.id),
            discount,
          ],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.deleteDiscount, (state, { cycleId, id }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          discounts: [...state.cycles.entities[cycleId].discounts.filter((d) => d.id !== id)],
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.setSelectedDiscountId, (state, { id }) => ({
    ...state,
    selectedDiscountId: id,
  })),
  on(cycleActions.upsertAnimalGroup, (state, { cycleId, animalGroup }) => ({
    ...state,
    cycles: adapter.updateOne(
      {
        id: cycleId,
        changes: {
          animalGroup,
        },
      },
      state.cycles,
    ),
  })),
  on(cycleActions.loadFarmResourcesSuccess, (state, { farmResources, cycleResources }) => ({
    ...state,
    farmResources,
    cycleResources,
    isLoaded: true,
  })),
  on(cycleActions.loadCarbonFootprintSuccess, (state, { data }) => ({
    ...state,
    carbonFootprint: data,
  })),
  on(cycleActions.resetCarbonFootprint, (state) => ({
    ...state,
    carbonFootprint: undefined as CarbonFootprintResponse,
  })),
  on(cycleActions.setSustainabilityNoticeVisibility, (state, { visible }) => ({
    ...state,
    sustainabilityNoticeVisible: visible,
  })),
);

export function reducer(state: CycleModuleState | undefined, action: Action) {
  return cycleReducer(state, action);
}
